import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { OrganizationLD, WebPageLD, BreadcrumbListLD } from '@/components/base/SEO';

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 桔子视频西瓜视频免费在线观看高清全集';
  }, []);

  const breadcrumbItems = [
    { name: '首页', url: 'https://juzivideo.com/' },
    { name: '关于我们', url: 'https://juzivideo.com/about' },
  ];

  return (
    <>
      <OrganizationLD />
      <BreadcrumbListLD items={breadcrumbItems} />
      <WebPageLD
        name="关于我们 - 桔子视频"
        description="桔子视频品牌介绍，了解桔子视频的企业文化、口碑评价与联系方式。提供西瓜视频、丝瓜视频高清全集不缓冲免费在线观看服务。"
        url="https://juzivideo.com/about"
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          {/* Breadcrumb */}
          <nav className="w-full px-4 md:px-6 py-4" aria-label="面包屑导航">
            <div className="max-w-5xl mx-auto flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-400 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">关于我们</span>
            </div>
          </nav>

          {/* Hero */}
          <section className="w-full px-4 md:px-6 py-12 md:py-16">
            <div className="max-w-5xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                关于桔子视频
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                桔子视频致力于为广大用户提供高清、流畅、免费的在线视频观看体验，
                汇聚西瓜视频、丝瓜视频等热门平台优质影视资源，打造一站式观影平台。
              </p>
            </div>
          </section>

          {/* Brand Story */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                    品牌故事
                  </h2>
                  <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      桔子视频成立于2023年，由一群热爱影视内容的年轻人创立。我们发现许多用户在寻找高清视频资源时，常常面临缓冲卡顿、资源不全、广告过多等问题。于是，我们决定打造一个真正以用户体验为核心的视频平台。
                    </p>
                    <p>
                      经过团队的不懈努力，桔子视频已经发展成为拥有海量影视资源的聚合平台。我们与多个优质内容源建立了深度合作，涵盖电视剧、电影、综艺、动漫、纪录片等多品类内容，满足不同用户的观影需求。
                    </p>
                    <p>
                      桔子视频的核心理念是"让每一个人都能轻松享受高品质影视内容"。我们通过先进的视频传输技术和智能推荐算法，为用户提供流畅不缓冲的高清观影体验和个性化的内容推荐。
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20tech%20office%20interior%20with%20warm%20ambient%20lighting%2C%20creative%20workspace%20with%20large%20monitors%20displaying%20video%20content%2C%20cozy%20atmosphere%2C%20dark%20theme%20with%20orange%20accent%20lighting%2C%20professional%20yet%20relaxed%20vibe&width=800&height=600&seq=juzi-about-brand&orientation=landscape"
                    alt="桔子视频品牌故事"
                    title="桔子视频团队 - 品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Reputation / Stats */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                口碑与数据
              </h2>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {[
                  { value: '5000万+', label: '月活跃用户', icon: 'ri-user-star-line' },
                  { value: '10万+', label: '影视资源', icon: 'ri-movie-line' },
                  { value: '99.7%', label: '播放成功率', icon: 'ri-check-double-line' },
                  { value: '4.8/5.0', label: '用户评分', icon: 'ri-star-line' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center p-5 rounded-lg bg-background-100 border border-background-200">
                    <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400">
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <div className="text-2xl font-bold text-foreground-950 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                      {stat.value}
                    </div>
                    <div className="text-xs text-foreground-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100/50">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                用户评价
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {[
                  {
                    name: '张先生',
                    role: '影视爱好者',
                    text: '桔子视频真的太棒了！西瓜视频资源全、更新快，丝瓜视频画质高清不缓冲，看剧体验一流。强烈推荐给所有追剧的朋友！',
                    rating: 5,
                  },
                  {
                    name: '李女士',
                    role: '全职妈妈',
                    text: '自从发现了桔子视频，每天的追剧时光变得更加美好。电视剧大全分类清晰，找剧特别方便，而且完全免费，太良心了！',
                    rating: 5,
                  },
                  {
                    name: '王同学',
                    role: '大学生',
                    text: '作为动漫迷，桔子视频的国漫资源让我非常满意。斗罗大陆高清全集都可以免费看，画质清晰流畅，宿舍网络也能不缓冲播放。',
                    rating: 5,
                  },
                ].map((review) => (
                  <div key={review.name} className="p-5 rounded-lg bg-background-100 border border-background-200">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(review.rating)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-sm text-accent-400"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.text}</p>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center">
                        <span className="text-xs font-semibold text-primary-400">{review.name[0]}</span>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-950">{review.name}</div>
                        <div className="text-xs text-foreground-500">{review.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className="w-full px-4 md:px-6 py-10 md:py-14">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                <div className="flex flex-col items-center p-6 rounded-lg bg-background-100 border border-background-200 text-center">
                  <div className="w-12 h-12 mb-3 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400">
                    <i className="ri-mail-line text-2xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                  <p className="text-xs text-foreground-500">contact@juzivideo.com</p>
                </div>
                <div className="flex flex-col items-center p-6 rounded-lg bg-background-100 border border-background-200 text-center">
                  <div className="w-12 h-12 mb-3 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400">
                    <i className="ri-phone-line text-2xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">客服热线</h3>
                  <p className="text-xs text-foreground-500">400-888-9999</p>
                </div>
                <div className="flex flex-col items-center p-6 rounded-lg bg-background-100 border border-background-200 text-center">
                  <div className="w-12 h-12 mb-3 flex items-center justify-center rounded-xl bg-primary-500/10 text-primary-400">
                    <i className="ri-map-pin-line text-2xl"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h3>
                  <p className="text-xs text-foreground-500">北京市朝阳区建国路88号</p>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}